import { cn } from "@/lib/cn";
import type { LucideIcon } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import { Sparkline, makeSpark } from "./sparkline";

export type KpiTileTone = "cyan" | "magenta" | "purple" | "orange";
export type KpiTileEnv = "dev" | "prod";

export interface KpiTileProps {
  /** KPI metric label (e.g. "Groups", "Ingestion rate") */
  label: string;
  /** Display value — can be formatted string like "3,985" */
  value: ReactNode;
  /** Optional unit displayed smaller next to value (e.g. "msg/hr", "%") */
  unit?: string;
  /** Lucide icon component for the icon chip */
  icon: LucideIcon;
  /** Color tone for the icon chip — overridden by env when present */
  tone?: KpiTileTone;
  /** Environment tag — drives border glow color via data-env attribute */
  env?: KpiTileEnv;
  /** Sparkline data — auto-generated from label if omitted */
  spark?: number[];
  className?: string;
  style?: CSSProperties;
}

/**
 * KPI metric card with neon glow border, icon chip, large value display,
 * and sparkline chart. Uses iris-kpi-tile CSS classes from kit.css.
 *
 * The `data-env` attribute controls the glow color:
 * - dev = cyan
 * - prod = purple
 */
export function KpiTile({
  label,
  value,
  unit,
  icon: Icon,
  tone = "cyan",
  env,
  spark,
  className,
  style,
}: KpiTileProps) {
  return (
    <div className={cn("iris-kpi-tile", className)} data-env={env} style={style}>
      <div className="iris-kpi-tile__head">
        <div className="iris-kpi-tile__label">{label}</div>
        <div className="iris-kpi-tile__icon" data-tone={tone}>
          <Icon size={14} />
        </div>
      </div>
      <div className="iris-kpi-tile__value">
        {value}
        {unit && <span className="iris-kpi-tile__unit">{unit}</span>}
      </div>
      <Sparkline values={spark ?? makeSpark(40, label.length)} height={44} />
    </div>
  );
}
